import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

export const CHART_SHARE_LABEL_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'always', label: 'Always' },
  { value: 'off', label: 'Off' },
];

export const getChartShareOptionValue = (option) => (
  option?.value ?? option?.ticker ?? option?.id ?? option?.field ?? option
);

export const getChartShareOptionLabel = (option) => (
  option?.label ?? option?.name ?? option?.company ?? option?.ticker ?? option?.field ?? option
);

export function ChartShareSelect({ id, label, value, options = [], onChange, disabled = false }) {
  if (!options.length) return null;
  const availableValues = options.map(getChartShareOptionValue);
  const resolvedValue = availableValues.some((option) => String(option) === String(value))
    ? value
    : availableValues[0];

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={resolvedValue ?? ''}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={String(getChartShareOptionValue(option))} value={getChartShareOptionValue(option)}>
            {getChartShareOptionLabel(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function ChartShareToggle({ label, ariaLabel, value, options = [], onChange }) {
  if (!options.length) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value}
        onChange={(event, next) => { if (next != null) onChange(next); }}
        aria-label={ariaLabel}
      >
        {options.map((option) => (
          <ToggleButton key={String(getChartShareOptionValue(option))} value={getChartShareOptionValue(option)}>
            {option.sublabel || option.label || getChartShareOptionValue(option)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

export function ChartShareMultiSelect({ id, label, value = [], options = [], onChange, allowEmpty = false }) {
  if (!options.length) return null;
  return (
    <FormControl fullWidth size="small">
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        multiple
        label={label}
        value={value}
        onChange={(event) => {
          const next = typeof event.target.value === 'string'
            ? event.target.value.split(',')
            : event.target.value;
          if (allowEmpty || next.length) onChange(next);
        }}
        renderValue={(selected) => `${selected.length} ${label}`}
      >
        {options.map((option) => {
          const resolvedValue = getChartShareOptionValue(option);
          return (
            <MenuItem key={String(resolvedValue)} value={resolvedValue}>
              <Checkbox checked={value.includes(resolvedValue)} size="small" />
              <ListItemText primary={getChartShareOptionLabel(option)} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export function ChartShareRange({ label, unit, value, min = 1, max, onChange, detail }) {
  if (!Number.isFinite(max) || max < 1) return null;
  const resolvedMin = Math.min(Math.max(1, min || 1), max);
  const resolvedValue = Math.min(max, Math.max(resolvedMin, Number(value) || resolvedMin));
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Last {resolvedValue} {resolvedValue === 1 ? unit.replace(/s$/, '') : unit}
        </Typography>
      </Box>
      <Slider
        size="small"
        min={resolvedMin}
        max={max}
        value={resolvedValue}
        onChange={(event, next) => onChange(next)}
        valueLabelDisplay="auto"
        aria-label={`Export time frame in ${unit}`}
      />
      {detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
          {detail}
        </Typography>
      ) : null}
    </Box>
  );
}

export function ChartShareNumber({ label, value, min = 0, max, step = 1, onChange }) {
  return (
    <TextField
      type="number"
      size="small"
      fullWidth
      label={label}
      value={value}
      inputProps={{ min, max, step }}
      onChange={(event) => onChange(Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min, Number(event.target.value) || 0)))}
    />
  );
}

const updateChartState = (setChartState, values) => (
  setChartState((current) => ({ ...current, ...values }))
);

/** Configuration-driven controls shared by every stateful chart export adapter. */
export function ChartShareControlSet({ controls = [], chartState, setChartState }) {
  return (
    <Stack spacing={2}>
      {controls.map((control) => {
        const value = chartState[control.stateKey];
        const change = (next) => updateChartState(setChartState, {
          [control.stateKey]: next,
          ...(control.onChange?.(next, chartState) || {}),
        });
        if (control.hidden) return null;
        if (control.type === 'select') {
          return <ChartShareSelect key={control.id} {...control} value={value} onChange={change} />;
        }
        if (control.type === 'multiSelect') {
          return <ChartShareMultiSelect key={control.id} {...control} value={value || []} onChange={change} />;
        }
        if (control.type === 'toggle') {
          return <ChartShareToggle key={control.id} {...control} value={value} onChange={change} />;
        }
        if (control.type === 'range') {
          return <ChartShareRange key={control.id} {...control} value={value} onChange={change} />;
        }
        if (control.type === 'number') {
          return <ChartShareNumber key={control.id} {...control} value={value} onChange={change} />;
        }
        return null;
      })}
    </Stack>
  );
}

export default ChartShareControlSet;
